import type { DocumentTypeStore } from '@/features/docs/DocumentTypeStore'
import type { DocumentType } from '@/features/docs/DocumentType'
import { action, observable, runInAction } from 'mobx'
import { onAbort } from '@/utils/abort-util'
import { task } from 'mobx-task'
import { memoize } from '@/utils/memoize-util'
import type { Id } from '@/infra/id'

/**
 * Delegate for prompting for file type assignment.
 */
export type PromptAssignFileTypesFn = (
  files: File[],
  documentTypeId?: Id,
  signal?: AbortSignal,
) => Promise<PromptResult>

/**
 * View model for assigning types to documents pending upload.
 */
export class AssignFileTypeDialogViewModel {
  /**
   * The currently active request for type assignment.
   */
  @observable accessor currentRequest: PromptRequest | null = null

  /**
   * Available document types.
   */
  @observable accessor availableTypes: DocumentType[] = []

  constructor(private readonly _documentTypeStore: DocumentTypeStore) {
    // Memoize the types.
    this.listTypes = this.listTypes.wrap((f) =>
      memoize(f, {
        promise: ([sig]) => sig,
      }),
    )
  }

  /**
   * Prompts for document types to be assigned to the specified files.
   *
   * @param files The files to prompt document types for.
   * @param documentTypeId If specified, fetches the document type and skips
   *  the prompt.
   * @param signal Abort signal which closes the modal and returns `null`
   *  if triggered.
   * @returns
   *  A promise with a map of each file to upload and their type.
   *  If a file is not present in the map, then the user removed it,
   *  indicating they no longer wish to upload that file.
   *  If a file is present but has a `null` value, then no type was chosen
   *  for that file.
   */
  async prompt(
    files: File[],
    documentTypeId?: Id,
    signal?: AbortSignal,
  ): Promise<PromptResult> {
    if (this.currentRequest) {
      this.currentRequest.resolve(null)
    }

    if (signal?.aborted) {
      return null
    }

    // If a document type was specified, skip the prompt, fetch that type
    // and assign that to all the files.
    if (documentTypeId) {
      const type =
        await this._documentTypeStore.fetchDocumentType(documentTypeId)

      if (signal?.aborted) {
        return null
      }

      if (!type) {
        return null
      }

      return new Map(files.map((file) => [file, type]))
    }

    // Start listing the types.
    void this.listTypes(signal)

    const entries = observable.array(
      files.map((f) => {
        const vm = new AssignmentEntryViewModel(f, () => {
          entries.remove(vm)

          // When removing the last document, cancel the request
          if (entries.length === 0) {
            request.resolve(null)
          }
        })
        return vm
      }),
    )

    const request: PromptRequest = {
      entries,
      promise: Promise.resolve(null),
      dispose: onAbort(signal, () => {
        request.resolve(null)
      }),
      resolve() {
        // will be replaced
      },
    }

    request.promise = new Promise<PromptResult>((resolve) => {
      request.resolve = action((result) => {
        resolve(result)
        request.dispose()
        this.currentRequest = null
      })
    })

    runInAction(() => (this.currentRequest = request))
    return request.promise
  }

  /**
   * Lists the available document types.
   */
  listTypes = task(async (signal?: AbortSignal) => {
    const documentTypes = await this._documentTypeStore.listTypes(signal)

    runInAction(() => (this.availableTypes = documentTypes))
  })

  /**
   * Cancel any ongoing request.
   */
  @action.bound
  cancel() {
    this.currentRequest?.resolve(null)
  }

  /**
   * Gathers the assigned files and resolves the current request.
   */
  @action.bound
  confirm() {
    if (!this.currentRequest) {
      return
    }

    const result = new Map<File, DocumentType | null>(
      this.currentRequest.entries.map((vm) => [vm.file, vm.type]),
    )

    this.currentRequest.resolve(result)
  }
}

type PromptResult = Map<File, DocumentType | null> | null

/**
 * An ongoing prompt request.
 */
export interface PromptRequest {
  entries: AssignmentEntryViewModel[]
  promise: Promise<PromptResult>
  dispose(): void
  resolve(result: PromptResult): void
}

/**
 * View model for an assignment entry.
 */
export class AssignmentEntryViewModel {
  /**
   * The assigned document type.
   */
  @observable accessor type: DocumentType | null = null

  constructor(
    readonly file: File,
    private readonly _remove: () => void,
  ) {}

  /**
   * Sets the type.
   *
   * @param type
   */
  @action.bound
  setType(type: DocumentType) {
    this.type = type
  }

  /**
   * Removes the entry.
   */
  @action.bound
  remove() {
    this._remove()
  }
}
